import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import update from 'immutability-helper';

// Mixins / Modules
import isResizable from '../../../../mixins/resizable';
import { isEmptyObject } from '../../../../../modules/global';

// Models
import Query from '@/models/query';
import User from '../../../../../models/v2/user';

// Base
import Button from '../../../../v5/base/button';
import Col from '../../../../v5/base/col';
import GoogleButton from '../../../../v5/base/google_button'
import Row from '../../../../v5/base/row';
import Separator from '../../../../v5/base/separator';

// Form
import AvatarUpload from '../../../../v5/public/users/sign_up/avatar_upload';
import Checkbox from '../../../../v5/form/checkbox';
import Input from '../../../../v5/form/input';
import PasswordStrengthChecklist from '../../../../v5/form/password_strength_checklist';
import Turnstile from '../../../../v5/public/users/sign_up/turnstile';

class SignUp extends React.Component {
  static propTypes = {
    user: PropTypes.object.isRequired,
    opportunity: PropTypes.object.isRequired,
    onSignUp: PropTypes.func.isRequired,
    onSetView: PropTypes.func.isRequired,
  }

  constructor(props) {
    super(props);

    this.state = {
      user: props.user,
      errorMessages: {},
      sending: false,
      turnstileToken: null,
      turnstileResetKey: 0,
    }
  }

  handleInputChange = (field, value) => {
    const { user } =
      this.state;

    const newUser = update(
      user,
      {
        [field]: {
          $set: value,
        },
      }
    );

    this.setState({
      user: newUser,
    });
  }

  handleProfileInputChange = (field, value) => {
    const { user } =
      this.state;

    const newUser = update(
      user,
      {
        profile_attributes: {
          [field]: {
            $set: value,
          },
        },
      }
    );

    this.setState({
      user: newUser,
    });
  }

  handleValidate = () => {
    this.setState({
      sending: true,
    });
  }

  handleError = () => {
    this.setState({
      sending: false,
    });
  }

  handleTurnstileTokenChange = (turnstileToken) => {
    this.setState((state) => ({
      turnstileToken,
      errorMessages: {
        ...state.errorMessages,
        turnstile: turnstileToken ? null : state.errorMessages.turnstile,
      },
    }));
  }

  handleSubmit = (e) => {
    const {
      onSignUp,
      opportunity,
    } = this.props;

    const {
      sending,
      turnstileToken,
      user,
    } = this.state;

    e && e.preventDefault();

    if (sending) return;

    if (!turnstileToken) {
      this.setState({ errorMessages: { turnstile: ['Please complete the verification.'] } });
      return;
    }

    this.setState(
      {
        sending: true,
      },
      () => {
        const profile =
          isEmptyObject(
            user.profile_attributes
          )
            ? {}
            : user.profile_attributes;

        const data = {
          turnstile_token: turnstileToken,
          user: {
            email: user.email,
            password:
              user.password || '',
            user_type:
              User.CONSULTANT,
            allow_marketing:
              user.allow_marketing,
            profile_attributes: {
              first_name:
                profile.first_name || '',
              last_name:
                profile.last_name || '',
              remote_avatar_url:
                profile.remote_avatar_url || '',
            },
          },
          opportunity_id:
            opportunity.id,
        };

        Query
          .request(
            '/users.json',
            {
              method: 'POST',
              body: data,
            }
          )
          .then((response) => {
            gtag(
              'event',
              'conversion',
              {
                'send_to':
                  'AW-707878932/-M1tCP7KxKsBEJTAxdEC',
              }
            );

            const {
              user,
              location,
            } = response;

            onSignUp(
              location,
              user
            );
          })
          .catch(
            ({
              responseJSON,
            }) => {
              this.setState({
                errorMessages:
                  (
                    responseJSON ||
                    {}
                  ).errors ||
                  {},
                sending: false,
                turnstileToken: null,
                turnstileResetKey: this.state.turnstileResetKey + 1,
              });
            }
          );
      }
    );
  }

  handleGoogleAuth = (response) => {
    const { onSignUp } =
      this.props;

    const {
      location,
      user,
    } = response;

    onSignUp(
      location,
      user
    );
  }

  render() {
    const {
      onSetView,
      opportunity,
    } = this.props;

    const {
      user,
      sending,
      errorMessages,
      turnstileResetKey,
    } = this.state;

    return (
      <React.Fragment>
        <div className='width-535 margin-center relative'>
          <img
            src={`${gon.HUBSPOT_ASSETS_URL}/oomple/assets/app/v5/layout/nav_logo.png`}
            className='height-28 margin-top-50 margin-bottom-25'
          />

          <div className='margin-bottom-30 xlg-font semi-bold line-height-26'>
            {
              opportunity.id &&
              <span>
                Join today to explore more jobs.
              </span>
            }

            {
              !opportunity.id &&
              <span>
                Welcome to Oomple, Join Today!
              </span>
            }
          </div>

          <div className='width-475'>
            <div className='margin-bottom-20 width-100pct flex'>
              <div className='margin-right-auto margin-left-auto'>
                <GoogleButton
                  text='signup_with'
                  onAuth={this.handleGoogleAuth}
                  onValidate={this.handleValidate}
                  onError={this.handleError}
                  userType={User.CONSULTANT}
                />
              </div>
            </div>

            <div className='flex justify-space-between vertical-align margin-bottom-20'>
              <div>
                <Separator className='width-155 margin-top-14 margin-bottom-14' />
              </div>

              <div>Or</div>

              <div>
                <Separator className='width-155 margin-top-14 margin-bottom-14' />
              </div>
            </div>

            <form onSubmit={this.handleSubmit}>
              <Row nested>
                <Col s={12}>
                  <AvatarUpload
                    value={
                      user.profile_attributes.remote_avatar_url
                    }
                    onChange={
                      (value) =>
                        this.handleProfileInputChange(
                          'remote_avatar_url',
                          value
                        )
                    }
                  />
                </Col>

                <Col s={12} m={6}>
                  <Input
                    id='first_name'
                    label='First Name'
                    value={
                      user.profile_attributes.first_name
                    }
                    required
                    errorMessage={
                      errorMessages['profile.first_name']
                    }
                    onChange={
                      (_, value) =>
                        this.handleProfileInputChange(
                          'first_name',
                          value
                        )
                    }
                  />
                </Col>

                <Col s={12} m={6}>
                  <Input
                    id='last_name'
                    label='Last Name'
                    value={
                      user.profile_attributes.last_name
                    }
                    required
                    errorMessage={
                      errorMessages['profile.last_name']
                    }
                    onChange={
                      (_, value) =>
                        this.handleProfileInputChange(
                          'last_name',
                          value
                        )
                    }
                  />
                </Col>

                <Col s={12}>
                  <Input
                    id='signup_email'
                    label='Email address'
                    value={user.email}
                    required
                    errorMessage={
                      errorMessages.email
                    }
                    onChange={
                      (_, value) =>
                        this.handleInputChange(
                          'email',
                          value
                        )
                    }
                  />
                </Col>

                <Col s={12}>
                  <Input
                    id='signup_password'
                    label='Create Password'
                    value={
                      user.password || ''
                    }
                    required
                    errorMessage={
                      errorMessages.password
                    }
                    type='password'
                    onChange={
                      (_, value) =>
                        this.handleInputChange(
                          'password',
                          value
                        )
                    }
                    className={
                      classNames(
                        '',
                        {
                          'margin-bottom-10':
                            !errorMessages.password,
                        }
                      )
                    }
                    allowPasswordToggle
                  />

                  <PasswordStrengthChecklist
                    password={
                      user.password || ''
                    }
                  />
                </Col>

                <Col s={12}>
                  <Checkbox
                    id='allow_marketing'
                    className='xxsm-font'
                    checked={
                      user.allow_marketing
                    }
                    onChange={
                      (_, checked) =>
                        this.handleInputChange(
                          'allow_marketing',
                          checked
                        )
                    }
                    label='I want to stay looped in with helpful newsletters, product updates and events.'
                  />
                </Col>

                <Col s={12}>
                  <Turnstile
                    errorMessage={errorMessages.turnstile}
                    onTokenChange={this.handleTurnstileTokenChange}
                    resetKey={turnstileResetKey}
                  />
                </Col>

                <Col s={12}>
                  <Button
                    id='continue'
                    text='Continue'
                    onClick={this.handleSubmit}
                    className='height-45 med-font vertical-align line-height-45'
                    block
                    disabled={sending}
                  />
                </Col>
              </Row>

              <button
                type='submit'
                className='hide'
              />
            </form>
          </div>

          <div className='xsm-font margin-bottom-15'>
            Have an account?{' '}

            <Button
              className='underline'
              text='Log In'
              size='custom'
              onClick={
                () =>
                  onSetView(
                    'sign_in'
                  )
              }
            />
          </div>

          <div className='margin-bottom-20 xxsm-font'>
            By signing up, you are agreeing to our{' '}

            <a
              className='primary-color hover-dark-primary'
              target='_blank'
              href='/terms_and_conditions'
            >
              Terms &amp; Conditions
            </a>.
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default isResizable(SignUp);
